import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { VentilationManuelle } from "@/components/VentilationManuelle";
import { 
  Search, Filter, CheckCircle, AlertCircle, Loader2, 
  ChevronLeft, ChevronRight, Save, Tag, Building2, Sparkles, Info, Percent,
  TrendingUp, TrendingDown, DollarSign
} from "lucide-react";
import { formatCurrency } from "@/lib/config";
import { toast } from "sonner";
import api from "@/lib/api";

// Départements disponibles
const DEPARTMENTS = ["PRQC", "LMDS", "Location", "GLOBAL"];

// Couleurs par département
const DEPT_COLORS = {
  "PRQC": "bg-blue-100 text-blue-700 border-blue-300",
  "LMDS": "bg-purple-100 text-purple-700 border-purple-300",
  "Location": "bg-amber-100 text-amber-700 border-amber-300",
  "GLOBAL": "bg-slate-100 text-slate-700 border-slate-300"
};

// Couleurs par niveau de profit
const NIVEAU_COLORS = {
  "Brut": "bg-blue-50 text-blue-600",
  "Marge": "bg-purple-50 text-purple-600",
  "Net": "bg-emerald-50 text-emerald-600",
  "Hors_Résultat": "bg-slate-50 text-slate-600"
};

export const ClassificationManuelle = () => {
  const [activeTab, setActiveTab] = useState("depenses");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [year, setYear] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Revenus non classifiés
  const [revenuesLoading, setRevenuesLoading] = useState(false);
  const [revenues, setRevenues] = useState([]);
  const [revenuesTotal, setRevenuesTotal] = useState(0);
  const [revenuesYear, setRevenuesYear] = useState(null);
  const [revenueSearch, setRevenueSearch] = useState("");
  const [savingRevKey, setSavingRevKey] = useState(null);

  // Sélections pour classification en lot
  const [selectedTxs, setSelectedTxs] = useState(new Set());
  const [bulkCategorie, setBulkCategorie] = useState("");
  const [bulkDepartement, setBulkDepartement] = useState("GLOBAL");
  const [bulkVentilationSuggestion, setBulkVentilationSuggestion] = useState(null);
  
  // IA Classification
  const [iaClassifying, setIaClassifying] = useState(false);
  const [iaResult, setIaResult] = useState(null);
  
  // Classifications individuelles avec suggestions
  const [txClassifications, setTxClassifications] = useState({});
  const [txVentilationSuggestions, setTxVentilationSuggestions] = useState({});
  const [showVentilationModal, setShowVentilationModal] = useState(false);
  const [ventilationTx, setVentilationTx] = useState(null);

  const LIMIT = 50;

  useEffect(() => { loadData(); }, [page, year]);
  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { if (activeTab === "revenus") loadRevenues(); }, [activeTab, revenuesYear]);

  const loadRevenues = async () => {
    setRevenuesLoading(true);
    try {
      let url = `${api.baseUrl}/classification/unclassified-revenues?limit=100&skip=0`;
      if (revenuesYear) url += `&year=${revenuesYear}`;
      const res = await axios.get(url);
      const data = res.data;
      setRevenues(data.transactions || []);
      setRevenuesTotal(data.total || 0);
    } catch (e) {
      toast.error("Erreur lors du chargement des revenus");
    } finally {
      setRevenuesLoading(false);
    }
  };

  const saveRevenueClassification = async (tx, dept) => {
    setSavingRevKey(tx.transaction_key);
    try {
      const res = await axios.post(`${api.baseUrl}/classification/manual-revenue`, { transaction_key: tx.transaction_key, departement: dept });
      if (res.status === 200) {
        toast.success(`Revenu classifié → ${dept}`);
        setRevenues(prev => prev.filter(r => r.transaction_key !== tx.transaction_key));
        setRevenuesTotal(prev => prev - 1);
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setSavingRevKey(null);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${api.baseUrl}/classification/categories`);
      const data = response.data;
      // Trier par catégorie
      const sorted = (data.categories || []).sort((a, b) => a.categorie.localeCompare(b.categorie));
      setCategories(sorted);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let url = `${api.baseUrl}/classification/unclassified?limit=${LIMIT}&skip=${page * LIMIT}`;
      if (year) url += `&year=${year}`;
      
      const response = await axios.get(url);
      const data = response.data;
      
      setTransactions(data.transactions || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // IA Classification en lot
  const handleIaClassify = async () => {
    const targetYear = year || 2025;
    setIaClassifying(true);
    setIaResult(null);
    try {
      const res = await axios.post(`${api.baseUrl}/finance/ia-classify-batch`, { year: targetYear, max: 200 });
      const data = res.data;
      if (res.status === 200) {
        setIaResult(data);
        toast.success(`${data.classified} transactions classifiées par IA (${data.ambiguous_a_valider} à valider)`);
        loadData(); // Recharger pour voir les changements
      } else {
        toast.error(data.detail || "Erreur classification IA");
      }
    } catch (e) {
      toast.error("Erreur réseau: " + e.message);
    } finally {
      setIaClassifying(false);
    }
  };

  // Calculer la ventilation suggérée pour une transaction
  const calculateVentilation = useCallback(async (categorie, compteComptable, montant, date) => {
    if (!categorie) return null;
    
    try {
      const params = new URLSearchParams({ categorie });
      if (compteComptable) params.append("compte_comptable", compteComptable);
      if (montant) params.append("montant", montant);
      if (date) params.append("date", date);
      
      const response = await axios.get(`${api.baseUrl}/ventilation/calculate?${params}`);
      const data = response.data;
      
      if (data.found && data.ventilation) {
        return {
          ventilation: data.ventilation,
          matchType: data.match_type
        };
      }
      return null;
    } catch (error) {
      console.error("Error calculating ventilation:", error);
      return null;
    }
  }, []);

  // Quand on sélectionne une catégorie pour une transaction individuelle
  const handleCategorySelect = async (txKey, categorie, tx) => {
    // Mettre à jour la classification
    setTxClassifications(prev => ({
      ...prev,
      [txKey]: {
        ...prev[txKey],
        categorie
      }
    }));
    
    // Calculer la suggestion de ventilation
    const suggestion = await calculateVentilation(
      categorie, 
      tx.account, 
      tx.credit || tx.debit,
      tx.date
    );
    
    if (suggestion) {
      setTxVentilationSuggestions(prev => ({
        ...prev,
        [txKey]: suggestion
      }));
      
      // Si une seule suggestion avec 100%, l'appliquer automatiquement
      if (suggestion.ventilation.length === 1 && suggestion.ventilation[0].pourcentage === 1) {
        setTxClassifications(prev => ({
          ...prev,
          [txKey]: {
            ...prev[txKey],
            categorie,
            departement: suggestion.ventilation[0].departement
          }
        }));
        toast.success(`Département suggéré: ${suggestion.ventilation[0].departement}`, {
          description: `Basé sur le compte ${tx.account}`
        });
      }
    } else {
      // Pas de suggestion, mettre GLOBAL par défaut
      setTxVentilationSuggestions(prev => {
        const newSuggestions = { ...prev };
        delete newSuggestions[txKey];
        return newSuggestions;
      });
    }
  };

  // Quand on sélectionne une catégorie pour le lot
  const handleBulkCategorySelect = async (categorie) => {
    setBulkCategorie(categorie);
    
    // Pour le lot, on ne peut pas suggérer car les transactions ont des comptes différents
    // Mais on peut chercher une règle par défaut (sans compte)
    const suggestion = await calculateVentilation(categorie, null, null, null);
    setBulkVentilationSuggestion(suggestion);
    
    if (suggestion && suggestion.ventilation.length === 1 && suggestion.ventilation[0].pourcentage === 1) {
      setBulkDepartement(suggestion.ventilation[0].departement);
    }
  };

  const toggleSelect = (txKey) => {
    const newSelected = new Set(selectedTxs);
    if (newSelected.has(txKey)) {
      newSelected.delete(txKey);
    } else {
      newSelected.add(txKey);
    }
    setSelectedTxs(newSelected);
  };

  const selectAll = () => {
    if (selectedTxs.size === transactions.length) {
      setSelectedTxs(new Set());
    } else {
      setSelectedTxs(new Set(transactions.map(t => t.transaction_key)));
    }
  };

  const updateTxClassification = (txKey, field, value) => {
    setTxClassifications(prev => ({
      ...prev,
      [txKey]: {
        ...prev[txKey],
        [field]: value
      }
    }));
  };

  const saveIndividual = async (tx) => {
    const classification = txClassifications[tx.transaction_key];
    if (!classification?.categorie) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }

    try {
      const cat = categories.find(c => c.categorie === classification.categorie);
      
      const payload = {
        transaction_key: tx.transaction_key,
        categorie: classification.categorie,
        departement: classification.departement || "GLOBAL",
        type_depense: cat?.type_depense || "",
        niveau_profit: cat?.niveau_profit || "",
        note: ""
      };
      
      // Si createRule est activé, ajouter les infos de règle
      if (classification.createRule) {
        payload.create_auto_rule = true;
        payload.rule_data = {
          mot_cle: tx.name || tx.memo,
          champ: "name",
          categorie: classification.categorie,
          departement_cible: classification.departement || "GLOBAL",
          niveau_profit: cat?.niveau_profit || "Marge",
          type_depense: cat?.type_depense || "",
          actif: true,
          description: `Règle auto-créée pour ${tx.name || tx.memo}`
        };
      }
      
      const response = await axios.post(`${api.baseUrl}/classification/manual`, payload);

      if (response.status === 200) {
        const data = response.data;
        if (classification.createRule && data.rule_created) {
          toast.success(`Transaction classifiée et règle automatique créée! ✅`);
        } else {
          toast.success("Transaction classifiée");
        }
        // Retirer de la liste
        setTransactions(prev => prev.filter(t => t.transaction_key !== tx.transaction_key));
        setTotal(prev => prev - 1);
      }
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const saveBulk = async () => {
    if (selectedTxs.size === 0) {
      toast.error("Aucune transaction sélectionnée");
      return;
    }
    if (!bulkCategorie) {
      toast.error("Veuillez sélectionner une catégorie");
      return;
    }

    setSaving(true);
    try {
      const cat = categories.find(c => c.categorie === bulkCategorie);
      
      const classifications = Array.from(selectedTxs).map(txKey => ({
        transaction_key: txKey,
        categorie: bulkCategorie,
        departement: bulkDepartement,
        type_depense: cat?.type_depense || "",
        niveau_profit: cat?.niveau_profit || "",
        note: ""
      }));

      const response = await axios.post(`${api.baseUrl}/classification/manual/bulk`, classifications);

      if (response.status === 200) {
        const data = response.data;
        toast.success(`${data.saved} transactions classifiées`);
        setSelectedTxs(new Set());
        setBulkCategorie("");
        setBulkVentilationSuggestion(null);
        loadData();
      }
    } catch (error) {
      console.error("Error bulk saving:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  // Appliquer la suggestion de ventilation
  const applySuggestion = (txKey, departement) => {
    setTxClassifications(prev => ({
      ...prev,
      [txKey]: {
        ...prev[txKey],
        departement
      }
    }));
  };

  const filteredTransactions = transactions.filter(tx => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      (tx.name || '').toLowerCase().includes(search) ||
      (tx.memo || '').toLowerCase().includes(search) ||
      (tx.account || '').toLowerCase().includes(search)
    );
  });

  const totalPages = Math.ceil(total / LIMIT);

  // Trouver la catégorie avec ses infos
  const getCategoryInfo = (catName) => categories.find(c => c.categorie === catName);

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="classification-manuelle-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Classification QB</h1>
            <p className="text-slate-500 text-sm">Classifiez les transactions QuickBooks non reconnues automatiquement</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="ia-classify-btn"
              onClick={handleIaClassify}
              disabled={iaClassifying}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {iaClassifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {iaClassifying ? 'Classification IA en cours...' : 'Classifier avec IA'}
            </Button>
            {iaResult && (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                {iaResult.classified} classifiées · {iaResult.ambiguous_a_valider} à valider
              </Badge>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="depenses" className="flex items-center gap-1.5" data-testid="tab-depenses">
              <TrendingDown className="w-4 h-4" />
              Dépenses
              {total > 0 && <Badge className="ml-1 bg-amber-500 text-white text-xs px-1.5 py-0">{total}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="revenus" className="flex items-center gap-1.5" data-testid="tab-revenus-classif">
              <TrendingUp className="w-4 h-4" />
              Revenus
              {revenuesTotal > 0 && <Badge className="ml-1 bg-blue-500 text-white text-xs px-1.5 py-0">{revenuesTotal}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* ===== ONGLET DÉPENSES (existant) ===== */}
          <TabsContent value="depenses" className="mt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-slate-500">
                  {total} dépense(s) à classifier
                  <span className="ml-2 text-xs text-blue-600">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    Suggestion automatique du département activée
                  </span>
                </p>
                <Select value={year?.toString() || "all"} onValueChange={(v) => setYear(v === "all" ? null : parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Année" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>

        {/* Classification en lot */}
        {selectedTxs.size > 0 && (
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                <Badge variant="secondary" className="text-lg px-4 py-2">
                  {selectedTxs.size} sélectionnées
                </Badge>
                
                <Select value={bulkCategorie} onValueChange={handleBulkCategorySelect}>
                  <SelectTrigger className="w-64">
                    <SelectValue placeholder="Catégorie..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {categories.map(cat => (
                      <SelectItem key={cat.categorie} value={cat.categorie}>
                        <div className="flex items-center gap-2">
                          <span>{cat.categorie}</span>
                          {cat.niveau_profit && (
                            <span className={`text-xs px-1 rounded ${NIVEAU_COLORS[cat.niveau_profit] || ''}`}>
                              {cat.niveau_profit}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="flex items-center gap-2">
                  <Select value={bulkDepartement} onValueChange={setBulkDepartement}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DEPARTMENTS.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {bulkVentilationSuggestion && (
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700">
                          <Sparkles className="w-3 h-3 mr-1" />
                          Suggestion par défaut
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        {bulkVentilationSuggestion.ventilation.map((v, i) => (
                          <div key={v.id || v._id || i}>{v.departement}: {(v.pourcentage * 100).toFixed(0)}%</div>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                
                <Button onClick={saveBulk} disabled={saving || !bulkCategorie}>
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Classifier tout
                </Button>
                
                <Button variant="outline" onClick={() => setSelectedTxs(new Set())}>
                  Annuler
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom, mémo ou compte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Transactions list */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Transactions non classifiées
              </CardTitle>
              <Button variant="outline" size="sm" onClick={selectAll}>
                {selectedTxs.size === transactions.length ? "Désélectionner tout" : "Sélectionner tout"}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                <p>Toutes les transactions sont classifiées!</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTransactions.map((tx, idx) => {
                  const suggestion = txVentilationSuggestions[tx.transaction_key];
                  const classification = txClassifications[tx.transaction_key];
                  const catInfo = getCategoryInfo(classification?.categorie);
                  
                  return (
                    <div 
                      key={`${tx.transaction_key || 'tx'}_${idx}`}
                      className={`p-4 border rounded-lg transition-colors ${
                        selectedTxs.has(tx.transaction_key) ? 'bg-blue-50 border-blue-300' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        {/* Checkbox */}
                        <Checkbox
                          checked={selectedTxs.has(tx.transaction_key)}
                          onCheckedChange={() => toggleSelect(tx.transaction_key)}
                        />
                        
                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-slate-500">{tx.date}</span>
                            <Badge variant="outline" className="text-xs">
                              {tx.year}
                            </Badge>
                          </div>
                          <p className="font-medium text-slate-900 truncate">
                            {tx.name || tx.memo || 'Sans nom'}
                          </p>
                          {tx.memo && tx.name && (
                            <p className="text-sm text-slate-500 truncate">{tx.memo}</p>
                          )}
                          <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {tx.account}
                          </p>
                        </div>
                        
                        {/* Montant - CORRIGÉ avec z-index */}
                        <div className="text-right relative z-10">
                          {tx.debit > 0 && (
                            <p className="font-bold text-red-600 bg-white px-2 py-1 rounded">{formatCurrency(tx.debit)}</p>
                          )}
                          {tx.credit > 0 && (
                            <p className="font-bold text-emerald-600 bg-white px-2 py-1 rounded">{formatCurrency(tx.credit)}</p>
                          )}
                        </div>
                        
                        {/* Classification individuelle */}
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2">
                            <Select 
                              value={classification?.categorie || ""}
                              onValueChange={(v) => handleCategorySelect(tx.transaction_key, v, tx)}
                            >
                              <SelectTrigger className="w-52">
                                <SelectValue placeholder="Catégorie..." />
                              </SelectTrigger>
                              <SelectContent className="max-h-80">
                                {categories.map(cat => (
                                  <SelectItem key={cat.categorie} value={cat.categorie}>
                                    <div className="flex items-center gap-2">
                                      <span>{cat.categorie}</span>
                                      {cat.niveau_profit && (
                                        <span className={`text-xs px-1 rounded ${NIVEAU_COLORS[cat.niveau_profit] || ''}`}>
                                          {cat.niveau_profit}
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            <Select 
                              value={classification?.departement || "GLOBAL"}
                              onValueChange={(v) => updateTxClassification(tx.transaction_key, 'departement', v)}
                            >
                              <SelectTrigger className={`w-32 ${
                                classification?.departement && DEPT_COLORS[classification.departement] 
                                  ? `border ${DEPT_COLORS[classification.departement]}` 
                                  : ''
                              }`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {DEPARTMENTS.map(d => (
                                  <SelectItem key={d} value={d}>
                                    <span className={DEPT_COLORS[d] ? `px-2 py-0.5 rounded ${DEPT_COLORS[d]}` : ''}>
                                      {d}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            
                            <Button 
                              size="sm" 
                              onClick={() => saveIndividual(tx)}
                              disabled={!classification?.categorie}
                            >
                              <CheckCircle className="w-4 h-4" />
                            </Button>

                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setVentilationTx(tx);
                                setShowVentilationModal(true);
                              }}
                              disabled={!classification?.categorie}
                              className="border-blue-200 hover:bg-blue-50"
                            >
                              <Percent className="w-4 h-4 mr-1" />
                              Ventilation
                            </Button>
                          </div>
                          
                          {/* Checkbox pour créer règle automatique */}
                          {classification?.categorie && (
                            <div className="flex items-center gap-2 text-xs mt-1">
                              <Checkbox
                                id={`auto-rule-${tx.transaction_key}`}
                                checked={classification?.createRule || false}
                                onCheckedChange={(checked) => updateTxClassification(tx.transaction_key, 'createRule', checked)}
                              />
                              <label 
                                htmlFor={`auto-rule-${tx.transaction_key}`}
                                className="text-slate-600 cursor-pointer hover:text-slate-900 flex items-center gap-1"
                              >
                                <Sparkles className="w-3 h-3 text-amber-500" />
                                Créer règle automatique pour "{tx.name?.substring(0, 30)}{tx.name?.length > 30 ? '...' : ''}"
                              </label>
                            </div>
                          )}
                          
                          {/* Suggestion de ventilation */}
                          {suggestion && suggestion.ventilation.length > 0 && (
                            <div className="flex items-center gap-2 text-xs">
                              <Sparkles className="w-3 h-3 text-amber-500" />
                              <span className="text-slate-500">Suggestion:</span>
                              {suggestion.ventilation.map((v, i) => (
                                <button
                                  key={v.id || v._id || i}
                                  onClick={() => applySuggestion(tx.transaction_key, v.departement)}
                                  className={`px-2 py-0.5 rounded border hover:opacity-80 transition-opacity ${
                                    DEPT_COLORS[v.departement] || 'bg-slate-100'
                                  } ${classification?.departement === v.departement ? 'ring-2 ring-offset-1 ring-blue-500' : ''}`}
                                >
                                  {v.departement} {v.pourcentage < 1 ? `(${(v.pourcentage * 100).toFixed(0)}%)` : ''}
                                </button>
                              ))}
                              <span className="text-slate-400">
                                ({suggestion.matchType === 'compte_comptable' ? 'par compte' : 
                                  suggestion.matchType === 'montant_exact' ? 'par montant' : 'par défaut'})
                              </span>
                            </div>
                          )}
                          
                          {/* Info catégorie */}
                          {catInfo && (
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <Info className="w-3 h-3" />
                              {catInfo.type_depense} • {catInfo.nature_depense}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Page {page + 1} sur {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
            </div>
          </TabsContent>

          {/* ===== ONGLET REVENUS ===== */}
          <TabsContent value="revenus" className="mt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-slate-500">
                  <DollarSign className="w-3.5 h-3.5 inline mr-1 text-blue-500" />
                  {revenuesTotal} revenu(s) QB sans correspondance automatique
                  <span className="ml-2 text-xs text-slate-400">— LMDS (compte 1020) et revenus matchés par facture sont exclus</span>
                </p>
                <div className="flex items-center gap-2">
                  <Select value={revenuesYear?.toString() || "all"} onValueChange={(v) => setRevenuesYear(v === "all" ? null : parseInt(v))}>
                    <SelectTrigger className="w-32"><SelectValue placeholder="Année" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes</SelectItem>
                      <SelectItem value="2024">2024</SelectItem>
                      <SelectItem value="2025">2025</SelectItem>
                      <SelectItem value="2026">2026</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" onClick={loadRevenues} disabled={revenuesLoading} data-testid="refresh-revenues-btn">
                    {revenuesLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Actualiser"}
                  </Button>
                </div>
              </div>

              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, mémo ou compte..."
                  value={revenueSearch}
                  onChange={e => setRevenueSearch(e.target.value)}
                  className="pl-10"
                  data-testid="revenue-search-input"
                />
              </div>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    Revenus à classifier
                  </CardTitle>
                  <CardDescription>
                    Assignez un département (PRQC, LMDS, LOCATION) à chaque transaction. La classification est sauvegardée et prioritaire au prochain calcul du dashboard.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {revenuesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                  ) : revenues.filter(r => {
                    if (!revenueSearch) return true;
                    const s = revenueSearch.toLowerCase();
                    return (r.name||'').toLowerCase().includes(s) || (r.memo||'').toLowerCase().includes(s);
                  }).length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                      <p className="font-medium">Aucun revenu à classifier !</p>
                      <p className="text-sm mt-1 text-slate-400">Tous les revenus QB sont automatiquement reconnus ou déjà classifiés.</p>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="revenues-list">
                      {revenues.filter(r => {
                        if (!revenueSearch) return true;
                        const s = revenueSearch.toLowerCase();
                        return (r.name||'').toLowerCase().includes(s) || (r.memo||'').toLowerCase().includes(s);
                      }).map((rev, idx) => (
                        <div key={rev.transaction_key || idx}
                          className="p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                          data-testid={`revenue-row-${idx}`}>
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className="text-xs text-slate-400">{rev.date}</span>
                                <Badge variant="outline" className="text-xs">{rev.type}</Badge>
                                <Badge className={`text-xs border ${DEPT_COLORS[rev.dept_auto] || 'bg-slate-100'}`}>
                                  Auto: {rev.dept_auto}
                                </Badge>
                              </div>
                              <p className="font-medium text-slate-900 truncate">{rev.name || 'Sans nom'}</p>
                              {rev.memo && <p className="text-sm text-slate-500 truncate mt-0.5">{rev.memo}</p>}
                              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                                <Building2 className="w-3 h-3" />
                                {rev.account}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2 shrink-0">
                              <p className="text-lg font-bold text-emerald-700">{formatCurrency(rev.montant_ttc)}</p>
                              <p className="text-xs text-slate-400">HT: {formatCurrency(rev.montant_ht)}</p>
                              <div className="flex gap-1.5" data-testid={`dept-buttons-${idx}`}>
                                {[
                                  { dept: "PRQC", color: "bg-blue-100 hover:bg-blue-200 text-blue-700 border-blue-300" },
                                  { dept: "LMDS", color: "bg-purple-100 hover:bg-purple-200 text-purple-700 border-purple-300" },
                                  { dept: "LOCATION", color: "bg-amber-100 hover:bg-amber-200 text-amber-700 border-amber-300" },
                                ].map(({ dept, color }) => (
                                  <button
                                    key={dept}
                                    onClick={() => saveRevenueClassification(rev, dept)}
                                    disabled={savingRevKey === rev.transaction_key}
                                    data-testid={`classify-${dept}-${idx}`}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded border transition-all disabled:opacity-50 ${color}`}
                                  >
                                    {savingRevKey === rev.transaction_key ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : dept}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal Ventilation Manuelle */}
      {showVentilationModal && ventilationTx && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="max-w-3xl w-full">
            <VentilationManuelle
              montant={ventilationTx.debit || ventilationTx.credit}
              categorie={txClassifications[ventilationTx.transaction_key]?.categorie || "Non classifié"}
              initialVentilation={txClassifications[ventilationTx.transaction_key]?.ventilation_manuelle}
              onSave={(ventilation) => {
                updateTxClassification(ventilationTx.transaction_key, 'ventilation_manuelle', ventilation);
                setShowVentilationModal(false);
                setVentilationTx(null);
                toast.success("Ventilation manuelle enregistrée");
              }}
              onCancel={() => {
                setShowVentilationModal(false);
                setVentilationTx(null);
              }}
            />
          </div>
        </div>
      )}
    </TooltipProvider>
  );
};

export default ClassificationManuelle;
